import { useContext, useEffect, useMemo, useState } from "react";
import { CircularProgress, Stack } from "@mui/material";
import type { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import type {
  ExtendedThalamosUser,
  FormContextData,
  InterviewWithParticipants,
  PatientTimelineResponse,
  WorkItem,
} from "@thalamos/aspire-common";
import {
  canAmhpSearchS12Doctors,
  canDoctorInitiateS12Payments,
  formIsApplication,
  formIsMedRec,
  isIndependentUserSession,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { LoggedInUserContext } from "../../Contexts.js";
import {
  isNotFoundStatus,
  isRecordId,
  throwUnansweredReadFailure,
} from "../../helpers/recordRead.js";
import { usePatientTimeline } from "../../hooks/apiCalls.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { EditMhaStatusDialog } from "../EditMhaStatus/EditMhaStatusDialog.js";
import SelectedForm from "../FormUpload/SelectedForm.js";
import { PatientReconciliationSearchModal } from "../PatientSearchV2/PatientReconciliationSearchModal.js";
import { AdmissionsFormPage } from "./admission/AdmissionsFormPage.js";
import { AmhpPaymentRequestReviewPopup } from "./admission/AmhpPaymentRequestReviewPopup.js";
import { AddFileNoteDialog } from "./admission/FileNoteDialogs/AddFileNoteDialog.js";
import { DeleteFileNoteDialog } from "./admission/FileNoteDialogs/DeleteFileNoteDialog.js";
import { InterviewManagementDialog } from "./admission/InterviewManagement/InterviewManagementDialog.js";
import { NO_MED_REC_REASON_OPTIONS } from "./admission/noMedRecReasonOptions.js";
import { RecordReasonDialog } from "./admission/RecordReasonDialog.js";
import {
  findDirectRedoRequestForUser,
  isAdmissionReadOnlyForRedo,
} from "./admission/redoRequest.js";
import { RequestAmendDialog } from "./admission/RequestAmendDialog.js";
import { RequestDeMinimisDialog } from "./admission/RequestDeMinimisDialog.js";
import { RequestRedoDialog } from "./admission/RequestRedoDialog.js";
import { S12DoctorRequestDialog } from "./admission/S12DoctorRequest/S12DoctorRequestDialog.js";
import { S12PaymentRequestDialog } from "./admission/S12PaymentRequestDialog.js";
import { canReconcileViaAspireFlag } from "./canReconcileViaAspireFlag.js";
import { FormContextActivityLog } from "./helpers/ActivityLogs/FormContextActivityLog.js";
import { BannerDialog } from "./helpers/BannerDialog.js";
import { admissionFormContextLogic } from "./helpers/formContextLogic.js";
import { RequestWorkItemDialog } from "./helpers/RequestWorkItemDialog.js";
import { UnfinaliseDialog } from "./helpers/UnfinaliseDialog.js";
import {
  hasNoActiveWorkItemOrRejectedCancelledOrCompleted,
  isAmendForOtherUser,
} from "./helpers/workItemHelpers.js";
import { McaFormPage } from "./mca/McaFormPage.js";
import { PoliceIncidentFormPage } from "./police-incident/PoliceIncidentFormPage.js";
import { SoadAssessmentFormPage } from "./soad-assessment/SoadAssessmentFormPage.js";
import { StandaloneFormPage } from "./standalone/StandaloneFormPage.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { S132PostSignPrompt } from "~/components/s132/S132PostSignPrompt.js";
import { useQueuedS132Prompt } from "~/components/s132/s132PromptQueue.js";

const NO_APP_REASON_RECORD_OPTIONS = [
  {
    value: "No evidence of mental disorder",
  },
  {
    value: "Legal criteria are not met",
  },
  {
    value: "Less restrictive treatment option is available",
  },
  {
    value: "Informal admission",
  },
  {
    value:
      "Objected by nearest relative (only applicable for s3 and s7 Applications)",
  },
  {
    value: "AMHP using their 14 days",
  },
  {
    value: "Other",
    descriptionRequired: true,
  },
];

type InterviewManagementLocationState = {
  initialView?: "choose-path" | "list";
};

const ADMISSION_REOPEN_OPTIONS = [
  {
    value: "Admission closed in error",
  },
  { value: "Patient clinical circumstances changed " },
  {
    value: "Patient withdrawn consent to informal admission",
  },
  {
    value: "Other",
    descriptionRequired: true,
  },
];

export function ModalDialogs({
  formContext,
  reloadFormContext,
  user,
  patientTimeline,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  user: ExtendedThalamosUser;
  patientTimeline: PatientTimelineResponse;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  const closeFormContextPage = () => {
    navigate(routeFns.formContextPage(formContext.id, formContext.patientId));
  };

  // Request modal
  const showRequestModal =
    location.pathname ===
    routeFns.formContextPageSendRequest(formContext.id, formContext.patientId);

  if (showRequestModal) {
    const requestModalParams = {
      requestType: qs.get("requestType") as WorkItem["requestType"] | null,
      formId: qs.get("formId") as string | undefined,
      formPart: qs.get("formPart") as string | undefined,
      title: qs.get("title") as string | undefined,
      infoMessage: qs.get("infoMessage") as string | undefined,
      openS12PaymentRequest: qs.get("openS12PaymentRequest") === "true",
      s12OutcomeType: qs.get("s12OutcomeType") as string | undefined,
      s12FormId: qs.get("s12FormId") as string | undefined,
      linkInterview: qs.get("linkInterview") === "true",
    };
    return (
      <RequestWorkItemDialog
        reloadFormContext={reloadFormContext}
        formContext={formContext}
        user={user}
        formId={requestModalParams.formId}
        title={requestModalParams.title}
        infoMessage={requestModalParams.infoMessage}
        openS12PaymentRequest={requestModalParams.openS12PaymentRequest}
        s12OutcomeType={requestModalParams.s12OutcomeType}
        s12FormId={requestModalParams.s12FormId}
        linkInterview={requestModalParams.linkInterview}
        closeFn={closeFormContextPage}
      />
    );
  }

  // Find Doctors dialog — URL-driven, must be checked BEFORE the generic
  // /interviews route because startsWith would also match.
  const findDoctorsPrefix = `${routeFns.formContextInterviews(formContext.id, formContext.patientId)}/`;
  const isFindDoctorsRoute =
    location.pathname.startsWith(findDoctorsPrefix) &&
    location.pathname.endsWith("/find-doctors");

  if (isFindDoctorsRoute) {
    if (
      !user.sessionOrganisationConfiguration ||
      !canAmhpSearchS12Doctors(user.sessionOrganisationConfiguration)
    ) {
      navigate(
        routeFns.formContextPage(formContext.id, formContext.patientId),
        { replace: true },
      );
      return null;
    }

    // Extract interviewId from the URL
    const interviewIdSegment = location.pathname
      .replace(findDoctorsPrefix, "")
      .replace("/find-doctors", "");

    return (
      <FindDoctorsFromRoute
        interviewId={interviewIdSegment}
        user={user}
        onClose={closeFormContextPage}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  // Interview Management modal (replaces S12 Doctor Request modal)
  const showInterviewManagementDialog = location.pathname.startsWith(
    routeFns.formContextInterviews(formContext.id, formContext.patientId),
  );

  if (showInterviewManagementDialog) {
    const interviewManagementLocationState =
      location.state as InterviewManagementLocationState | null;

    // Feature flag check - redirect if not enabled
    if (
      !user.sessionOrganisationConfiguration ||
      !canAmhpSearchS12Doctors(user.sessionOrganisationConfiguration)
    ) {
      navigate(
        routeFns.formContextPage(formContext.id, formContext.patientId),
        { replace: true },
      );
      return null;
    }

    return (
      <InterviewManagementDialog
        formContextId={formContext.id}
        formContext={formContext}
        user={user}
        initialView={interviewManagementLocationState?.initialView}
        onClose={closeFormContextPage}
        reloadFormContext={reloadFormContext}
        hasActiveWorkItem={
          !hasNoActiveWorkItemOrRejectedCancelledOrCompleted(formContext)
        }
      />
    );
  }

  // S12 Payment Request modal (for doctors to submit payment requests)
  const showS12PaymentRequestDialog =
    location.pathname ===
    routeFns.formContextS12PaymentRequest(
      formContext.id,
      formContext.patientId,
    );

  if (showS12PaymentRequestDialog) {
    // Parse query params for hints
    const searchParams = new URLSearchParams(location.search);
    const outcomeType = searchParams.get("outcomeType");
    const formIdParam = searchParams.get("formId");

    const hints =
      outcomeType || formIdParam
        ? {
            initialOutcomeType: outcomeType as
              "form" | "no-med-rec" | "completed-outside-thalamos" | undefined,
            initialFormId: formIdParam || undefined,
          }
        : undefined;

    return (
      <S12PaymentRequestDialog
        formContext={formContext}
        user={user}
        onClose={closeFormContextPage}
        reloadFormContext={reloadFormContext}
        hints={hints}
      />
    );
  }

  // S12 Payment Request Review modal (for AMHPs to review pending requests)
  const showS12PaymentRequestReviewDialog =
    location.pathname ===
    routeFns.formContextS12PaymentRequestReview(
      formContext.id,
      formContext.patientId,
    );

  if (showS12PaymentRequestReviewDialog) {
    return (
      <AmhpPaymentRequestReviewPopup
        formContext={formContext}
        onClose={closeFormContextPage}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  // Success modal
  const showSuccessModal =
    location.pathname ===
    routeFns.formContextPageSuccessDialog(
      formContext.id,
      formContext.patientId,
    );

  if (showSuccessModal) {
    const successModalParams = {
      title: qs.get("title") as string | undefined,
      message: qs.get("message") as string | undefined,
    };
    return (
      <BannerDialog
        title={successModalParams.title || ""}
        message={successModalParams.message || ""}
        onClose={() =>
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
          )
        }
      />
    );
  }

  // Activity Log modal
  const showActivityLogModal = location.pathname.startsWith(
    routeFns.formContextPageActivityLog(formContext.id, formContext.patientId),
  );
  if (showActivityLogModal) {
    return (
      <FormContextActivityLog
        formContext={formContext}
        onClose={closeFormContextPage}
        user={user}
      />
    );
  }

  const showConfirmFinaliseDialog = location.pathname.startsWith(
    routeFns.formContextPageConfirmFinaliseDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  const showEditMhaStatusDialog = location.pathname.startsWith(
    routeFns.formContextPageEditMhaStatusDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  const showDeleteUploadedFormDialog = location.pathname.startsWith(
    routeFns.formContextDeleteUploadedFormDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  const hasFormWithApplicationOrMedRec = formContext.forms.some(
    (f) => formIsApplication(f) || formIsMedRec(f),
  );

  const onClose = () => {
    navigate(routeFns.formContextPage(formContext.id, formContext.patientId), {
      replace: true,
    });
  };

  const {
    canLaunchMedicalRecommendation,
    isAdmissionClosed,
    isAdmissionArchivedOrClosed,
    canLaunchApplication,
    hasActiveAmend,
  } =
    formContext.type === "admission"
      ? admissionFormContextLogic(formContext, user)
      : {};

  // Compute hasActiveDeMinimis for all form context types
  const hasActiveDeMinimis = formContext.workItems?.some(
    (wi) =>
      wi.requestType === "de-minimis" &&
      (wi.status === "pending" || wi.status === "accepted"),
  );

  if (showConfirmFinaliseDialog) {
    const itemName = qs.get("itemName") ?? "item";

    const extraWarnings: string[] = [];
    if (hasActiveAmend) {
      extraWarnings.push(
        "This admission has an active Section 15 amend. This will be cancelled once you mark scrutiny as complete",
      );
    }
    if (hasActiveDeMinimis) {
      extraWarnings.push(
        "There is an active de minimis correction in progress. This will be cancelled once you mark scrutiny as complete",
      );
    }

    return (
      <ConfirmationModal
        extraWarning={
          extraWarnings.length > 0 ? extraWarnings.join(". ") : undefined
        }
        message={
          hasFormWithApplicationOrMedRec
            ? t("forms.completionConfirmationMessageWithRestrictions")
            : formContext.type === "soad-assessment"
              ? t("forms.completionConfirmationMessageWithSrfRestrictions")
              : t("forms.completionConfirmationMessageWithNoteRestriction")
        }
        confirmFn={async () => {
          const finalised = await api.work
            .finalise(formContext.activeTeamworkWorkItem!.id)
            .then(
              (result) => result.status === 204,
              () => false,
            );
          if (finalised) {
            renderSuccessToast({
              message: "Administrative scrutiny marked as complete",
            });
            reloadFormContext();
            navigate(
              routeFns.formContextPageSuccessDialog(
                formContext.id,
                formContext.patientId,
                {
                  title: "Administrative scrutiny marked complete",
                  message: `You have successfully marked administrative scrutiny of this ${itemName} complete`,
                },
              ),
            );
          } else {
            renderErrorToast({
              message: "Error marking administrative scrutiny as complete",
            });
          }
        }}
        closeFn={onClose}
      />
    );
  }

  if (showEditMhaStatusDialog) {
    return (
      <EditMhaStatusDialog
        hasFormWithApplicationOrMedRec={hasFormWithApplicationOrMedRec}
        formContext={formContext}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  // Success modal
  const showUnfinaliseDialog =
    location.pathname ===
    routeFns.formContextUnfinaliseDialog(formContext.id, formContext.patientId);

  if (showUnfinaliseDialog && user.sessionContext?.teamType === "mha") {
    return (
      <UnfinaliseDialog
        formContext={formContext}
        onClose={onClose}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  const showConfirmDeleteDraftDialog = location.pathname.startsWith(
    routeFns.formContextPageConfirmDeleteDraftDialog(
      formContext.id,
      formContext.patientId,
    ),
  );
  const draftFormId = qs.get("draftFormId");
  if (showConfirmDeleteDraftDialog && draftFormId) {
    return (
      <ConfirmationModal
        message={
          "Please confirm you want to delete this form. This will delete all drafts and any requests you have sent to other teams or individuals to work on it. It cannot be undone."
        }
        confirmFn={async () => {
          const result = await api.forms.delete(draftFormId);
          if (result.status === 204) {
            renderSuccessToast({
              message: "Form deleted",
            });
            // Deleting the only form leaves nothing to come back to, so the user
            // goes to the patient instead of an empty form context.
            const formsRemain =
              formContext.forms.filter(
                (f) => f.id !== draftFormId && f.status !== "deleted",
              ).length > 0;

            if (
              formContext.type === "admission" ||
              formContext.type === "police-incident" ||
              formsRemain
            ) {
              navigate(
                routeFns.formContextPage(formContext.id, formContext.patientId),
              );
              reloadFormContext();
            } else {
              navigate(routeFns.patientHome(formContext.patientId));
            }
          } else {
            renderErrorToast({
              message: "Error deleting form",
            });
          }
        }}
        closeFn={onClose}
      />
    );
  }

  // The below modals are only applicable to admissions / standalone
  if (
    formContext.type === "police-incident" ||
    formContext.type === "soad-assessment"
  ) {
    return null;
  }

  const showRequestAmendDialog = location.pathname.startsWith(
    routeFns.formContextPageRequestAmendDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  const formId = qs.get("formId");
  const formTemplateId = qs.get("formTemplateId");
  if (showRequestAmendDialog && formId && formTemplateId) {
    return (
      <RequestAmendDialog
        reloadFormContext={reloadFormContext}
        formContext={formContext}
        formId={formId}
        formTemplateId={formTemplateId}
        onClose={onClose}
      />
    );
  }

  const showRequestDeMinimisDialog = location.pathname.startsWith(
    routeFns.formContextPageRequestDeMinimisDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  if (showRequestDeMinimisDialog && formId && formTemplateId) {
    return (
      <RequestDeMinimisDialog
        reloadFormContext={reloadFormContext}
        formContext={formContext}
        formId={formId}
        formTemplateId={formTemplateId}
        onClose={onClose}
      />
    );
  }

  const showRequestRedoDialog = location.pathname.startsWith(
    routeFns.formContextPageRequestRedoDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  // A redo cannot be requested on an archived or closed admission. This
  // backstops direct navigation to the route (e.g. a bookmarked URL): fall
  // through to the admission view rather than render an inert dialog.
  if (
    showRequestRedoDialog &&
    formId &&
    formTemplateId &&
    !isAdmissionArchivedOrClosed
  ) {
    return (
      <RequestRedoDialog
        reloadFormContext={reloadFormContext}
        formContext={formContext}
        formId={formId}
        formTemplateId={formTemplateId}
        onClose={onClose}
      />
    );
  }

  if (showDeleteUploadedFormDialog && formId) {
    return (
      <ConfirmationModal
        message={
          "Please confirm you want to delete this uploaded form. This action cannot be undone."
        }
        confirmFn={() => {
          void (async () => {
            const result = await api.forms.deleteUploadedForm(formId);
            if (result.status === 204) {
              renderSuccessToast({
                message: "Uploaded form deleted",
              });
              navigate(
                routeFns.formContextPage(formContext.id, formContext.patientId),
              );
              reloadFormContext();
            } else {
              renderErrorToast({
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                message: `Error deleting uploaded form ${(result.data as any)?.message}`,
              });
            }
          })();
        }}
        closeFn={onClose}
      />
    );
  }

  const showAddFileNoteDialog = location.pathname.startsWith(
    routeFns.formContextAddFileNoteDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  const showFormUploadSelectedForm = location.pathname.startsWith(
    routeFns.formUploadSelectedTemplate(formContext.id, formContext.patientId),
  );

  if (showAddFileNoteDialog && formId) {
    return (
      <AddFileNoteDialog
        reloadFormContext={reloadFormContext}
        closeFn={onClose}
        patientId={formContext.patientId}
        formContextId={formContext.id}
        formId={formId}
        user={user}
      />
    );
  }

  if (showFormUploadSelectedForm) {
    return (
      <SelectedForm
        formContext={formContext}
        closeFn={onClose}
        patientTimeline={patientTimeline}
      />
    );
  }
  const showDeleteFileNoteDialog = location.pathname.startsWith(
    routeFns.formContextDeleteFileNoteDialog(
      formContext.id,
      formContext.patientId,
    ),
  );

  if (showDeleteFileNoteDialog && formId) {
    return (
      <DeleteFileNoteDialog
        reloadFormContext={reloadFormContext}
        closeFn={onClose}
        patientId={formContext.patientId}
        formContextId={formContext.id}
        formId={formId}
        user={user}
      />
    );
  }

  // Reason for not proceeding modal
  const showReasonForNotProceedingDialog = location.pathname.startsWith(
    routeFns.formContextReasonForNotProceedingDialog(
      formContext.id,
      formContext.patientId,
    ),
  );
  if (
    showReasonForNotProceedingDialog &&
    (canLaunchApplication || canLaunchMedicalRecommendation)
  ) {
    type ReasonApiCallback = (
      workItemId: string,
      reason: string,
      reasonDescription: string | undefined,
    ) => Promise<AxiosResponse>;

    async function onConfirmReason(
      reason: string,
      reasonDescription: string | undefined,
      apiCb: ReasonApiCallback,
      successMessage: string,
      isMedRecAction?: boolean,
    ) {
      const workItemId = formContext.activeTeamworkWorkItem!.id;
      const result = await apiCb(workItemId, reason, reasonDescription);

      if (result.status === 204) {
        renderSuccessToast({ message: "Action successfully confirmed" });
        // Recording the reason archives the admission, and navigating does not
        // refetch the form context.
        reloadFormContext();

        // For independent S12 doctors with the feature enabled, redirect to payment request
        // after "no med rec" action
        const isIndependentS12Doctor =
          isIndependentUserSession(user) &&
          user.sessionOrganisationConfiguration != null &&
          canDoctorInitiateS12Payments(user.sessionOrganisationConfiguration);

        if (isMedRecAction && isIndependentS12Doctor) {
          navigate(
            routeFns.formContextS12PaymentRequest(
              formContext.id,
              formContext.patientId,
              { outcomeType: "no-med-rec" },
            ),
            { replace: true },
          );
        } else {
          navigate(
            routeFns.formContextPageSuccessDialog(
              formContext.id,
              formContext.patientId,
              {
                title: "Action confirmed",
                message: successMessage,
              },
            ),
            { replace: true },
          );
        }
      } else {
        renderErrorToast({
          message:
            "Failed to record reason. Please try again later. If the problem persists, contact support.",
        });
      }
    }

    // No medrec / No application modals
    if (canLaunchMedicalRecommendation) {
      return (
        <RecordReasonDialog
          title="I will not make a medical recommendation"
          dropDownLabel="Please enter a reason why you have chosen not to make a medical recommendation"
          bannerTitle="The admission will be archived once you have confirmed this action. The admission can be unarchived should circumstances change."
          options={NO_MED_REC_REASON_OPTIONS}
          onClose={onClose}
          onConfirmClick={(reason, reasonDescription) => {
            const onConfirmNoMedRec = async (
              reason: string,
              reasonDescription: string | undefined,
            ) => {
              await onConfirmReason(
                reason,
                reasonDescription,
                api.admissions.recordReasonForNotProceedingMedRec,
                "You have successfully confirmed that you will not make a medical recommendation. All participants have been notified by email that you have confirmed this action",
                true, // isMedRecAction - for S12 payment request redirect
              );
            };
            void onConfirmNoMedRec(reason, reasonDescription);
          }}
        />
      );
    } else {
      return (
        <RecordReasonDialog
          title="I will not make an application form"
          dropDownLabel="Please enter a reason why you have chosen not to make an application form"
          bannerTitle="The admission will be archived once you have confirmed this action. The admission can be reopened should circumstances change."
          options={NO_APP_REASON_RECORD_OPTIONS}
          onClose={onClose}
          onConfirmClick={(reason, reasonDescription) => {
            const onConfirmNoApp = async (
              reason: string,
              reasonDescription: string | undefined,
            ) => {
              await onConfirmReason(
                reason,
                reasonDescription,
                api.admissions.recordReasonForNotProceedingAppForm,
                "You have successfully confirmed that you will not make an application form. All participants have been notified by email that you have confirmed this action",
              );
            };
            void onConfirmNoApp(reason, reasonDescription);
          }}
        />
      );
    }
  }

  // Reopen admission modal
  const showReopenAdmissionDialog = location.pathname.startsWith(
    routeFns.formContextReopenAdmissionDialog(
      formContext.id,
      formContext.patientId,
    ),
  );
  if (showReopenAdmissionDialog && isAdmissionClosed) {
    const workItemId = formContext.activeTeamworkWorkItem!.id;
    return (
      <RecordReasonDialog
        title="Reopen admission"
        bannerTitle="You can proceed working on the possible admission once you have confirmed this action. "
        dropDownLabel="Please enter a reason why you have chosen to reopen this admission"
        options={ADMISSION_REOPEN_OPTIONS}
        onClose={onClose}
        onConfirmClick={(reason, reasonDescription) => {
          const onConfirmReopen = async (
            reason: string,
            reasonDescription: string | undefined,
          ) => {
            const result = await api.admissions.reOpenAdmission(
              workItemId,
              reason,
              reasonDescription || undefined,
            );

            if (result.status === 204) {
              renderSuccessToast({
                message: "Admission has successfully been reopened",
              });
              navigate(
                routeFns.formContextPageSuccessDialog(
                  formContext.id,
                  formContext.patientId,
                  {
                    title: "Action confirmed",
                    message: `You have successfully reopened this admission. All participants have been notified by email that you have confirmed this action. `,
                  },
                ),
                { replace: true },
              );
            } else {
              renderErrorToast({
                message:
                  "Failed to reopen the admission. Please try again later. If the problem persists, contact support.",
              });
            }
          };
          void onConfirmReopen(reason, reasonDescription);
        }}
      />
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// FindDoctorsFromRoute — loads an interview by ID and opens the S12 dialog
// ---------------------------------------------------------------------------

function FindDoctorsFromRoute({
  interviewId,
  user,
  onClose,
  reloadFormContext,
}: {
  interviewId: string;
  user: ExtendedThalamosUser;
  onClose: () => void;
  reloadFormContext: () => void;
}) {
  const [interview, setInterview] = useState<InterviewWithParticipants | null>(
    null,
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    api.interviews
      .get(interviewId)
      .then((r) => setInterview(r.data))
      .catch(() => setError(true));
  }, [interviewId]);

  useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  if (!interview) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          mt: 8,
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <S12DoctorRequestDialog
      interview={interview}
      user={user}
      onClose={onClose}
      reloadFormContext={() => {
        reloadFormContext();
      }}
    />
  );
}

export function FormPageInner({
  formContext,
  patientId,
  reloadFormContext,
}: {
  formContext: FormContextData;
  patientId: string;
  reloadFormContext: () => void;
}) {
  const userContext = useContext(LoggedInUserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const user = userContext!.user;

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId,
  });

  const s132Prompt = useQueuedS132Prompt(formContext.id);

  const isReconciliationRoute = location.pathname.endsWith(
    routeFns.formContextPagePatientReconciliation(formContext.id, patientId),
  );

  // Aspire-level reconciliation eligibility (applies to all form contexts)
  const aspireCanReconcile = canReconcileViaAspireFlag({
    sessionOrganisationConfiguration: user?.sessionOrganisationConfiguration,
    patientHasNhsNumber: patientTimeline?.patient.nhsNumber != null,
  });

  // For non-police-incident contexts, build the onReconcileClick handler
  const reconcileClickHandler =
    formContext.type !== "police-incident" && aspireCanReconcile
      ? () => {
          navigate(
            routeFns.formContextPagePatientReconciliation(
              formContext.id,
              formContext.patientId,
            ),
          );
        }
      : undefined;

  // Redirect away from reconciliation route if not eligible (non-police-incident)
  useEffect(() => {
    if (!isReconciliationRoute) return;
    if (formContext.type === "police-incident") return; // handled in PoliceIncidentFormPage
    if (!patientTimeline) return; // wait for data

    if (!aspireCanReconcile) {
      navigate(
        routeFns.formContextPage(formContext.id, formContext.patientId),
        { replace: true },
      );
    }
  }, [
    isReconciliationRoute,
    aspireCanReconcile,
    patientTimeline,
    formContext.id,
    formContext.patientId,
    formContext.type,
    navigate,
  ]);

  // Show reconciliation modal for non-police-incident contexts
  if (
    isReconciliationRoute &&
    formContext.type !== "police-incident" &&
    patientTimeline &&
    aspireCanReconcile
  ) {
    const pdsOrg = user?.sessionOrganisationConfiguration
      ?.pdsEndUserOrganisationODSCode
      ? {
          odsCode:
            user.sessionOrganisationConfiguration.pdsEndUserOrganisationODSCode,
        }
      : undefined;

    return (
      <PatientReconciliationSearchModal
        open={true}
        onClose={() => {
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
            { replace: true },
          );
        }}
        onComplete={({ patientId: mergedPatientId }) => {
          reloadFormContext();
          reloadPatientTimeline();
          navigate(routeFns.formContextPage(formContext.id, mergedPatientId), {
            replace: true,
          });
        }}
        currentPatientId={formContext.patientId}
        formContextId={formContext.id}
        pdsOrg={pdsOrg}
        currentPatient={formContext.patient}
        currentPatientNhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
      />
    );
  }

  return (
    <>
      {patientTimeline && (
        <ModalDialogs
          patientTimeline={patientTimeline}
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          user={user}
        />
      )}

      <S132PostSignPrompt
        formContextId={formContext.id}
        patientId={formContext.patientId}
        patientName={`${formContext.patient.name.given} ${formContext.patient.name.family}`}
        recorderName={user.name}
        formContextPath={routeFns.formContextPage(formContext.id, patientId)}
        queuedFor={s132Prompt.queuedFor}
        onDone={s132Prompt.done}
      />

      {formContext.type === "admission" ? (
        <AdmissionsFormPage
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          user={user}
          patientTimeline={patientTimeline ?? null}
          reloadPatientTimeline={reloadPatientTimeline}
          onReconcileClick={reconcileClickHandler}
        />
      ) : formContext.type === "police-incident" ? (
        <PoliceIncidentFormPage
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          patientTimeline={patientTimeline ?? null}
          reloadPatientTimeline={reloadPatientTimeline}
          isReconciliationRoute={isReconciliationRoute}
        />
      ) : formContext.type === "soad-assessment" ? (
        <SoadAssessmentFormPage
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          patientTimeline={patientTimeline ?? null}
          reloadPatientTimeline={reloadPatientTimeline}
          onReconcileClick={reconcileClickHandler}
        />
      ) : formContext.type === "mca-assessment" ? (
        <McaFormPage
          formContext={formContext}
          user={user}
          reloadFormContext={reloadFormContext}
          patientTimeline={patientTimeline ?? null}
          reloadPatientTimeline={reloadPatientTimeline}
          onReconcileClick={reconcileClickHandler}
        />
      ) : (
        <StandaloneFormPage
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          patientTimeline={patientTimeline ?? null}
          reloadPatientTimeline={reloadPatientTimeline}
          onReconcileClick={reconcileClickHandler}
        />
      )}
    </>
  );
}

export function FormContextPage({}: DefaultPageProps) {
  const { formContextId, patientId } = useParams();

  if (!isRecordId(formContextId) || !isRecordId(patientId)) {
    return <Navigate to={routeFns.notFound()} replace />;
  }

  return <FormContextPageForId />;
}

function FormContextPageForId() {
  const { formContextId, patientId } = useParams();
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const currentUserId = userContext?.user?.id;
  const currentUserTeamId = userContext?.user?.sessionContext?.teamId;

  const [
    { data: form, loading: formLoading, response: formResponse, error },
    reloadFormContext,
  ] = apiHooks.forms.getFormContext(formContextId!);

  // The redo, if any, that the current user is responsible for accepting or
  // declining. A redo must be reviewed on its full-page preview (PDF + reason)
  // before it can be actioned, so an admission carrying one for this user is not
  // shown directly. Memoised for a stable identity in the redirect effect deps.
  const redoToReview = useMemo(
    () =>
      form && currentUserId
        ? findDirectRedoRequestForUser(form, currentUserId, currentUserTeamId)
        : undefined,
    [form, currentUserId, currentUserTeamId],
  );

  // An archived or closed admission is read-only for redo: the statuses stay
  // visible but no accept/decline is offered, so the author is never routed
  // into the preview — they land on the admission itself.
  const redoIsReadOnly = !!form && isAdmissionReadOnlyForRedo(form);

  useEffect(() => {
    if (!formLoading) {
      if (isNotFoundStatus(formResponse?.status)) {
        navigate(routeFns.notFound(), { replace: true });
      } else if (
        form &&
        form.activeTeamworkWorkItem?.status === "pending" &&
        !(
          currentUserId &&
          isAmendForOtherUser(form.activeTeamworkWorkItem, currentUserId)
        )
      ) {
        navigate(routeFns.formReview(form.id, form.patientId));
      } else if (form && redoToReview && !redoIsReadOnly) {
        navigate(routeFns.formRedoReview(form.id, form.patientId));
      }
    }
  }, [
    form,
    formLoading,
    formResponse?.status,
    currentUserId,
    redoToReview,
    redoIsReadOnly,
    navigate,
  ]);

  throwUnansweredReadFailure({ error, response: formResponse }, "form");

  if (form?.id && patientId) {
    return (
      <FormPageInner
        formContext={form!}
        patientId={patientId}
        reloadFormContext={reloadFormContext}
      />
    );
  }

  return (
    <Stack
      sx={{ justifyContent: "center", alignItems: "center", height: "100%" }}
    >
      <CircularProgress />
    </Stack>
  );
}
